import { throttle } from 'lodash';
import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { ID_SKIN_CONTAINER } from './types';

interface IPropTypes {
  children: React.ReactNode;
  onSkinSizeChanged?: (skinSizesInfo: ISkinSizesInfo) => void;
}

export const SkinResizeObserver: React.FC<IPropTypes> = React.memo(
  ({ children, onSkinSizeChanged }) => {
    const skinElement = useMemo<HTMLElement>(
      () => document.getElementById(ID_SKIN_CONTAINER) as HTMLElement,
      []
    );

    const resizeCallbacks = useMemo<Set<Callback>>(() => new Set(), []);

    const [skinSizes, setSkinSizes] = useState<ISkinSizesInfo>(() => {
      const width = skinElement.offsetWidth;

      return {
        skinWidth: width,
        skinHeight: skinElement.offsetHeight,
        isBigSkin: isBigSkin(width),
      };
    });

    const contextValue = useMemo<ISkinSizesContextValue>(() => {
      return {
        ...skinSizes,
        skinElement,
        subscribe: (cb) => {
          resizeCallbacks.add(cb);

          return () => {
            resizeCallbacks.delete(cb);
          };
        },
        unsubscribe: (cb) => {
          resizeCallbacks.delete(cb);
        },
      };
    }, [skinSizes, skinElement, resizeCallbacks]);

    const onObserve = useCallback(
      ([skinEntry]: ResizeObserverEntry[]) => {
        const { width, height } = skinEntry.contentRect;

        const newInfo: ISkinSizesInfo = {
          skinWidth: width,
          skinHeight: height,
          isBigSkin: isBigSkin(width),
        };

        setSkinSizes(newInfo);
        onSkinSizeChanged?.(newInfo);

        const cbArg = {
          ...newInfo,
          skinElement,
        };
        resizeCallbacks.forEach((cb) => {
          cb(cbArg);
        });
      },
      [onSkinSizeChanged, resizeCallbacks, skinElement]
    );

    useEffect(() => {
      const observer = new ResizeObserver(
        throttle(onObserve, 100, { leading: false, trailing: true })
      );
      observer.observe(skinElement);

      return () => {
        observer.disconnect();
      };
    }, [skinElement, onObserve]);

    useEffect(() => {
      return () => {
        resizeCallbacks.clear();
      };
    }, [resizeCallbacks]);

    return (
      <SkinSizesContext.Provider value={contextValue}>
        {children}
      </SkinSizesContext.Provider>
    );
  }
);
SkinResizeObserver.displayName = 'SkinResizeObserver';

interface ISkinSizesInfo {
  skinWidth: number;
  skinHeight: number;
  isBigSkin: boolean;
}

type ISkinInfo = ISkinSizesInfo & { skinElement: HTMLElement };

type ISkinSizesContextValue = ISkinInfo & {
  subscribe: (cb: Callback) => () => void;
  unsubscribe: (cb: Callback) => void;
};

type Callback = (contextValue: ISkinInfo) => void;

export const SkinSizesContext = createContext<ISkinSizesContextValue>({
  skinWidth: 0,
  skinHeight: 0,
  skinElement: document.body,
  isBigSkin: false,
  subscribe: () => () => void 0,
  unsubscribe: () => void 0,
});

function isBigSkin(width: number): boolean {
  return width >= 1600;
}
